import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Platform } from "@marketing/shared";
import { api, unwrap } from "@/lib/api";
import { useAuthStore } from "@/stores/authStore";

export type ApprovalVariant = {
  id: string;
  caption: string;
  hashtags: string[];
  status: string;
  socialAccount: { id: string; platform: Platform; displayName: string };
};

export type ApprovalPost = {
  id: string;
  topic: string | null;
  origin: string;
  status: string;
  scheduledAt: string | null;
  createdAt: string;
  pendingApprovalAt: string | null;
  regenerationCount: number;
  campaignId: string | null;
  monthlyPlanId: string | null;
  brand: { id: string; name: string; timezone: string };
  mediaAsset: { originalUrl: string; enhancedUrl: string | null } | null;
  variants: ApprovalVariant[];
};

/**
 * Everything the queue touches, invalidated together.
 *
 * A decision changes the queue, the calendar and the sidebar badge, and
 * forgetting one leaves a post visibly in two states at once.
 */
function refresh(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ["approvals"] });
  void qc.invalidateQueries({ queryKey: ["calendar"] });
  void qc.invalidateQueries({ queryKey: ["brands"] });
}

export function useApprovals(brandId: string | null) {
  const workspaceId = useAuthStore((s) => s.workspaceId);
  return useQuery({
    queryKey: ["approvals", workspaceId, brandId],
    queryFn: () =>
      unwrap<ApprovalPost[]>(
        api.get("/content/approvals", { params: brandId ? { brandId } : {} }),
      ),
    enabled: !!workspaceId,
  });
}

/** Drives the sidebar badge: a count, so it stays cheap to poll. */
export function useApprovalCount() {
  const workspaceId = useAuthStore((s) => s.workspaceId);
  return useQuery({
    queryKey: ["approvals", "count", workspaceId],
    queryFn: () => unwrap<{ waiting: number }>(api.get("/content/approvals/count")),
    enabled: !!workspaceId,
    staleTime: 30_000,
  });
}

export function useApprovePost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (postId: string) =>
      unwrap<{ outcome: string; scheduledAt: string | null }>(
        api.post(`/content/posts/${postId}/approve`, {}),
      ),
    onSuccess: () => refresh(qc),
  });
}

export function useSkipPost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (postId: string) =>
      unwrap<{ outcome: string }>(api.post(`/content/posts/${postId}/skip`, {})),
    onSuccess: () => refresh(qc),
  });
}

/** Rewrite, optionally with an instruction in the owner's own words. */
export function useRevisePost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { postId: string; guidance?: string }) =>
      unwrap<{ regenerationsLeft: number }>(
        api.post(`/content/posts/${args.postId}/regenerate`, {
          ...(args.guidance?.trim() ? { guidance: args.guidance.trim() } : {}),
        }),
      ),
    onSuccess: () => refresh(qc),
  });
}

/** Removes a post that never published. Its image follows after a grace period. */
export function useDeletePost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (postId: string) =>
      unwrap<{ deleted: true; imageOrphaned: boolean }>(
        api.delete(`/content/posts/${postId}`),
      ),
    onSuccess: () => refresh(qc),
  });
}

export function useApproveAll() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (filter: { brandId?: string; campaignId?: string; monthlyPlanId?: string }) =>
      unwrap<{ approved: number; alreadyHandled: number }>(
        api.post("/content/approvals/approve-all", filter),
      ),
    onSuccess: () => refresh(qc),
  });
}
